/**
 * Decision 0093: what a card is about, so a second card about the same thing replaces or refuses instead of piling up.
 * A plan card's subject is the set of (change kind, item) pairs it touches; an agent question's subject is its item,
 * its addressee and its option keys. Pure.
 */
import { isOpenProposal } from "./state"
import type { Actor, GraphCommand, ProjectState, Proposal, ProposalBody } from "./types"

/** The item a change is about, or null for changes that create or touch no existing item. */
const itemOfChange = (c: GraphCommand): string | null => {
  switch (c._tag) {
    case "UpdateItem":
    case "ReviseChecks":
    case "CloseItem":
    case "MoveItem":
    case "AssignItem":
      return c.itemId
    default:
      return null
  }
}

/** "CloseItem:itm_1|ReviseChecks:itm_2", or null when any change has no existing item (a new item is never a duplicate). */
export const planSubjectKey = (changes: ReadonlyArray<GraphCommand>): string | null => {
  const parts = new Set<string>()
  for (const c of changes) {
    const id = itemOfChange(c)
    if (id === null) return null
    parts.add(`${c._tag}:${id}`)
  }
  return parts.size > 0 ? [...parts].sort().join("|") : null
}

type Question = Extract<ProposalBody, { _tag: "question" }>

/** An agent's question about an item: item, option keys and blocks; null for escalations, asks, topics and no options. */
export const questionSubjectKey = (body: Question, blocks: Proposal["blocks"]): string | null => {
  if (body.escalation !== null || body.answersAsk !== null) return null
  if (body.subject?._tag !== "item" || body.options.length === 0) return null
  const keys = body.options.map((o) => o.key).sort()
  const gates = blocks.map((b) => `${b._tag}:${b.id}`).sort()
  return `${body.subject.id}|${keys.join(",")}|${gates.join(",")}`
}

const agentIdOf = (a: Actor): string | null => (a._tag === "agent" ? a.agentId : null)

/** Decision 0102: a card on its way up the chain counts for the person it ends with. */
const approverIdOf = (p: Pick<Proposal, "approver">): string | null =>
  p.approver._tag === "human" ? p.approver.userId : p.approver._tag === "agent" ? p.approver.escalateTo : null

/** How recently an answered question still counts as "just asked" for the same subject. */
export const RECENT_ANSWER_MS = 30 * 60_000

export type SameSubject =
  | { readonly _tag: "none" }
  /** The same agent's open card about the same subject: the new card replaces it. */
  | { readonly _tag: "supersede"; readonly proposal: Proposal }
  /** Another agent's open card, or this agent's card the person answered minutes ago: the new card is a duplicate. */
  | { readonly _tag: "duplicate"; readonly proposal: Proposal; readonly answered: boolean }

/**
 * The open (or just answered) card about the same subject as a new agent card, for the same person. Only agents'
 * cards: system escalations keep their own one-per-item rule (runs.ts), people's cards are theirs.
 */
export const sameSubjectCard = (
  s: ProjectState,
  actor: Actor,
  approverId: string | null,
  subject: { readonly kind: "plan_change" | "question"; readonly key: string | null },
  now: number,
): SameSubject => {
  const me = agentIdOf(actor)
  if (me === null || subject.key === null || approverId === null) return { _tag: "none" }
  const keyOf = (p: Proposal): string | null =>
    p.body._tag === "plan_change" && subject.kind === "plan_change"
      ? planSubjectKey(p.body.changes)
      : p.body._tag === "question" && subject.kind === "question"
        ? questionSubjectKey(p.body, p.blocks)
        : null
  const same = s.proposals.filter(
    (p) => agentIdOf(p.proposedBy) !== null && approverIdOf(p) === approverId && keyOf(p) === subject.key,
  )
  const open = same.find((p) => isOpenProposal(p) || p.state === "revision_requested")
  if (open)
    return agentIdOf(open.proposedBy) === me
      ? { _tag: "supersede", proposal: open }
      : { _tag: "duplicate", proposal: open, answered: false }
  const answered = same.findLast(
    (p) =>
      subject.kind === "question" &&
      agentIdOf(p.proposedBy) === me &&
      p.state === "answered" &&
      p.resolution !== null &&
      now - p.resolution.at <= RECENT_ANSWER_MS,
  )
  return answered ? { _tag: "duplicate", proposal: answered, answered: true } : { _tag: "none" }
}
